import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Heading, LoadingSpinner, PageContainer, Section } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  changeYowanLoginEmail,
  changeYowanPassword,
} from '../app/yowanAuthClient';
import { useYowanAuth } from '../app/YowanAuthContext';
import { useYowanAuthGuard } from '../app/yowanAuthGuards';
import {
  fetchYowanAccountNotificationPreferences,
  fetchYowanAdminMailSummary,
  fetchYowanCurrentMemberProfile,
  isYowanMemberProfileNotFoundError,
  updateYowanAccountNotificationPreferences,
} from '../app/yowanContentClient';
import { getStakeholderRoleState } from './StakeholderSeminarsPage';
const { yowanPageContent } = require('./pageContent');
const { getYowanRouteMeta } = require('../routes/manifest');

const qualificationLabelByKey = {
  member: '会員',
  instructor: '講師',
  organizer: '主催者',
};

const pickAccessArray = (source, candidateKeys) => {
  if (!source) {
    return [];
  }

  return candidateKeys.reduce((values, key) => {
    const value = source[key];

    if (Array.isArray(value)) {
      return [...values, ...value];
    }

    return values;
  }, []);
};

const addUniqueLabel = (labels, label) => {
  if (label && !labels.includes(label)) {
    labels.push(label);
  }
};

const normalizeQualificationKey = (value) => String(value || '').toLowerCase().replace(/[^a-z_:-]/g, '');

const addKnownQualificationLabel = (labels, value) => {
  const text = String(value || '');

  if (text.includes('講師')) {
    addUniqueLabel(labels, qualificationLabelByKey.instructor);
    return;
  }

  if (text.includes('主催者')) {
    addUniqueLabel(labels, qualificationLabelByKey.organizer);
    return;
  }

  if (text.includes('会員')) {
    addUniqueLabel(labels, qualificationLabelByKey.member);
  }
};

const normalizeAccessLabels = (labels) => {
  if (!Array.isArray(labels)) {
    return [];
  }

  return labels
    .map((label) => String(label || '').trim())
    .filter(Boolean)
    .filter((label, index, self) => self.indexOf(label) === index);
};

const hasBlogAuthorAccessLabel = (labels = []) =>
  Array.isArray(labels) &&
  labels.some((label) => {
    const text = String(label || '');
    return text.includes('ブログ執筆者') || text.toLowerCase().includes('blog_author');
  });

const buildMembershipLabels = ({ currentUser, auth, memberProfile }) => {
  const accessMe = auth?.accessMe || {};
  const accessLabels = normalizeAccessLabels(accessMe.access_labels || accessMe.accessLabels || []);

  if (accessLabels.length > 0) {
    return accessLabels;
  }

  const labels = [];
  const memberAccess = accessMe.member_access || accessMe.memberAccess || {};
  const canAccessAdmin = Boolean(
    currentUser.canAccessAdmin || auth?.adminStatus?.can_access_admin || accessMe.can_access_admin
  );

  if (memberProfile) {
    addUniqueLabel(labels, qualificationLabelByKey.member);
  }

  if (canAccessAdmin) {
    addUniqueLabel(labels, '管理者');
  }

  if (currentUser.canAccessBlogAuthor || hasBlogAuthorAccessLabel(accessLabels)) {
    addUniqueLabel(labels, 'ブログ執筆者');
  }

  [
    ...pickAccessArray(accessMe, [
      'qualification_keys',
      'member_qualification_keys',
      'member_access_kinds',
      'member_access_keys',
    ]),
    ...pickAccessArray(memberAccess, [
      'qualification_keys',
      'member_qualification_keys',
      'member_access_kinds',
      'member_access_keys',
    ]),
  ].forEach((key) => {
    const normalizedKey = normalizeQualificationKey(key);

    if (normalizedKey.includes('instructor')) {
      addUniqueLabel(labels, qualificationLabelByKey.instructor);
      return;
    }

    if (normalizedKey.includes('organizer')) {
      addUniqueLabel(labels, qualificationLabelByKey.organizer);
      return;
    }

    if (normalizedKey.includes('member')) {
      addUniqueLabel(labels, qualificationLabelByKey.member);
    }
  });

  [
    ...pickAccessArray(accessMe, [
      'qualification_labels',
      'member_qualification_labels',
      'member_access_labels',
      'access_labels',
    ]),
    ...pickAccessArray(memberAccess, [
      'qualification_labels',
      'member_qualification_labels',
      'member_access_labels',
      'access_labels',
    ]),
  ].forEach((label) => {
    addKnownQualificationLabel(labels, label);
  });

  if (accessMe.is_instructor || accessMe.can_instruct || memberAccess.is_instructor) {
    addUniqueLabel(labels, qualificationLabelByKey.instructor);
  }

  if (accessMe.is_organizer || accessMe.can_organize || memberAccess.is_organizer) {
    addUniqueLabel(labels, qualificationLabelByKey.organizer);
  }

  return labels.length ? labels : ['-'];
};

const buildProfileSummaryItems = (fields = [], memberProfile) =>
  fields.map((field) => ({
    ...field,
    value: memberProfile?.[field.key] || '',
  }));

const resolveLoginEmail = ({ currentUser = {}, auth = {}, memberProfile = null } = {}) => {
  const accessMe = auth?.accessMe || {};
  const instructorAccess = currentUser.instructorAccess || accessMe.instructorAccess || accessMe.instructor_access || {};
  const organizerAccess = currentUser.organizerAccess || accessMe.organizerAccess || accessMe.organizer_access || {};
  const blogAuthorAccess = currentUser.blogAuthorAccess || accessMe.blogAuthorAccess || accessMe.blog_author_access || {};

  return currentUser.email ||
    memberProfile?.email ||
    auth?.authMe?.email ||
    accessMe.email ||
    accessMe.login_email ||
    accessMe.loginEmail ||
    instructorAccess.loginEmail ||
    instructorAccess.login_email ||
    organizerAccess.loginEmail ||
    organizerAccess.login_email ||
    blogAuthorAccess.loginEmail ||
    blogAuthorAccess.login_email ||
    blogAuthorAccess.email ||
    '';
};

const buildAccountSummaryItems = ({ section = {}, loginEmail = '', membershipLabels = [] } = {}) =>
  (section.fields || []).map((field) => {
    if (field.key === 'email') {
      return {
        ...field,
        value: loginEmail || section.emptyEmailLabel || '-',
      };
    }

    if (field.key === 'roles') {
      return {
        ...field,
        value: membershipLabels.filter((label) => label && label !== '-').join(' / ') || '-',
      };
    }

    return {
      ...field,
      value: '-',
    };
  });

const getPreferenceEnabled = (bundle, category) =>
  bundle?.preferencesByCategory?.[category]?.isEnabled === true;

const formatUnreadCountLabel = (value) => {
  const count = Number(value || 0);

  if (!Number.isFinite(count) || count <= 0) {
    return '';
  }

  return count > 99 ? '99+' : String(count);
};

const getOperatorAccessSummary = (auth = {}) =>
  auth?.accessMe?.operator_access || auth?.accessMe?.operatorAccess || null;

const isOperatorProfileMissing = (auth = {}) => {
  const operatorAccess = getOperatorAccessSummary(auth);

  return Boolean(operatorAccess?.profile_missing === true || operatorAccess?.profileMissing === true);
};

const hasMemberProfileSignal = ({ currentUser = {}, auth = {} } = {}) => {
  const accessMe = auth?.accessMe || {};
  const accessLabels = normalizeAccessLabels(accessMe.access_labels || accessMe.accessLabels || []);
  const memberAccess = accessMe.member_access || accessMe.memberAccess || {};
  const memberProfile = accessMe.member || accessMe.yowan_member || accessMe.member_profile || accessMe.memberProfile || null;
  const hasMemberLabel = accessLabels.some((label) => String(label || '').includes('会員'));

  return Boolean(
    currentUser.canAccessAdmin ||
      memberProfile ||
      hasMemberLabel ||
      accessMe.is_member ||
      accessMe.isMember ||
      accessMe.has_member_profile ||
      accessMe.hasMemberProfile ||
      memberAccess.id ||
      memberAccess.member_id ||
      memberAccess.memberId ||
      memberAccess.is_active === true ||
      memberAccess.isActive === true
  );
};

const shouldLoadMemberProfile = ({ currentUser = {}, auth = {} } = {}) => {
  if (hasMemberProfileSignal({ currentUser, auth })) {
    return true;
  }

  if (currentUser.canAccessStakeholder || currentUser.canAccessBlogAuthor) {
    return false;
  }

  return true;
};

const buildInitialFormState = (sections = []) =>
  sections.reduce((accumulator, section) => {
    if (!section.form?.labels) {
      return accumulator;
    }

    accumulator[section.title] = Object.keys(section.form.labels).reduce((fieldAccumulator, fieldKey) => {
      fieldAccumulator[fieldKey] = '';
      return fieldAccumulator;
    }, {});

    return accumulator;
  }, {});

const buildValidationErrors = (section, values) => {
  const errors = {};

  if (section.kind === 'emailChange') {
    if (!values.currentPassword?.trim()) {
      errors.currentPassword = '現在のパスワードを入力してください。';
    }
    if (!values.newEmail?.trim()) {
      errors.newEmail = '新しいメールアドレスを入力してください。';
    }
  }

  if (section.kind === 'passwordChange') {
    if (!values.currentPassword?.trim()) {
      errors.currentPassword = '現在のパスワードを入力してください。';
    }
    if (!values.newPassword?.trim()) {
      errors.newPassword = '新しいパスワードを入力してください。';
    }
    if (!values.newPasswordConfirm?.trim()) {
      errors.newPasswordConfirm = '確認用の新しいパスワードを入力してください。';
    } else if (values.newPassword !== values.newPasswordConfirm) {
      errors.newPasswordConfirm = '確認用パスワードが一致しません。';
    }
  }

  return errors;
};

const buildEmptyFormValues = (section) =>
  Object.keys(section.form?.labels || {}).reduce((accumulator, fieldKey) => {
    accumulator[fieldKey] = '';
    return accumulator;
  }, {});

const getFieldInputType = (fieldKey) => {
  if (fieldKey.toLowerCase().includes('password')) {
    return 'password';
  }

  return 'email';
};

const buildStakeholderMenuLinks = (section = {}, currentUser = {}) => {
  const roleState = getStakeholderRoleState(currentUser);
  const canAccessStakeholder = Boolean(roleState.hasInstructorProfile || roleState.hasOrganizerProfile);

  return (section.links || []).filter((link) => {
    if (link.kind === 'dashboard') {
      return canAccessStakeholder;
    }

    if (link.kind === 'instructorProfile') {
      return roleState.hasInstructorProfile;
    }

    if (link.kind === 'organizerProfile') {
      return roleState.hasOrganizerProfile;
    }

    return false;
  });
};

const buildBlogAuthorMenuLinks = (section = {}, currentUser = {}) => {
  if (!currentUser.canAccessBlogAuthor) {
    return [];
  }

  return section.links || [];
};

const MyPageTopPage = ({ guardOptions }) => {
  const content = yowanPageContent['/mypage'];
  const memberProfileLoadErrorMessage = content?.memberProfileLoadErrorMessage || 'プロフィール情報を読み込めませんでした。';
  const metadata = getYowanRouteMeta('/mypage');
  const location = useLocation();
  const { refreshAuth, syncAuth } = useYowanAuth();
  const guard = useYowanAuthGuard({
    requiresAuth: true,
    search: location.search,
    ...guardOptions,
  });
  const currentUser = guard.currentUser;
  const canAccessAdminMail = Boolean(currentUser.canAccessAdmin);
  const shouldFetchMemberProfile = shouldLoadMemberProfile({ currentUser, auth: guard.auth });
  const [memberProfile, setMemberProfile] = useState(null);
  const [isMemberProfileMissing, setIsMemberProfileMissing] = useState(false);
  const [isMemberProfileLoading, setIsMemberProfileLoading] = useState(false);
  const [memberProfileLoadError, setMemberProfileLoadError] = useState('');
  const [accountPreferences, setAccountPreferences] = useState(null);
  const [isAccountPreferenceLoading, setIsAccountPreferenceLoading] = useState(false);
  const [accountPreferenceError, setAccountPreferenceError] = useState('');
  const [accountPreferenceMessage, setAccountPreferenceMessage] = useState('');
  const [submittingPreference, setSubmittingPreference] = useState('');
  const [adminMailSummary, setAdminMailSummary] = useState(null);
  const [isAdminMailSummaryLoading, setIsAdminMailSummaryLoading] = useState(false);
  const [adminMailSummaryError, setAdminMailSummaryError] = useState('');
  const [formValues, setFormValues] = useState(() => buildInitialFormState(content?.sections));
  const [formErrors, setFormErrors] = useState({});
  const [formMessages, setFormMessages] = useState({});
  const [submittingSection, setSubmittingSection] = useState('');
  const [accountNotice, setAccountNotice] = useState('');

  useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute('content', metadata.description);
      }
    }
  }, [metadata]);

  useEffect(() => {
    if (guard.status !== 'ready') {
      return undefined;
    }

    let isMounted = true;

    const loadMemberProfile = async () => {
      setIsMemberProfileLoading(true);
      setIsMemberProfileMissing(false);
      setMemberProfileLoadError('');
      setMemberProfile(null);

      if (!shouldFetchMemberProfile) {
        setIsMemberProfileMissing(true);
        setIsMemberProfileLoading(false);
        return;
      }

      try {
        const profile = await fetchYowanCurrentMemberProfile();

        if (!isMounted) {
          return;
        }

        setMemberProfile(profile);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        if (isYowanMemberProfileNotFoundError(error)) {
          setIsMemberProfileMissing(true);
        } else {
          setMemberProfileLoadError(memberProfileLoadErrorMessage);
        }
      } finally {
        if (isMounted) {
          setIsMemberProfileLoading(false);
        }
      }
    };

    loadMemberProfile();

    return () => {
      isMounted = false;
    };
  }, [guard.status, memberProfileLoadErrorMessage, shouldFetchMemberProfile]);

  useEffect(() => {
    if (guard.status !== 'ready') {
      return undefined;
    }

    let isMounted = true;

    const loadAccountPreferences = async () => {
      setIsAccountPreferenceLoading(true);
      setAccountPreferenceError('');
      setAccountPreferenceMessage('');

      try {
        const payload = await fetchYowanAccountNotificationPreferences();

        if (!isMounted) {
          return;
        }

        setAccountPreferences(payload);
      } catch (error) {
        if (isMounted) {
          setAccountPreferenceError(content?.accountNotificationLoadErrorMessage || 'メール配信設定を読み込めませんでした。');
        }
      } finally {
        if (isMounted) {
          setIsAccountPreferenceLoading(false);
        }
      }
    };

    loadAccountPreferences();

    return () => {
      isMounted = false;
    };
  }, [content?.accountNotificationLoadErrorMessage, guard.status]);

  useEffect(() => {
    if (guard.status !== 'ready' || !canAccessAdminMail) {
      setAdminMailSummary(null);
      setAdminMailSummaryError('');
      setIsAdminMailSummaryLoading(false);
      return undefined;
    }

    let isMounted = true;

    const loadAdminMailSummary = async () => {
      setIsAdminMailSummaryLoading(true);
      setAdminMailSummaryError('');

      try {
        const payload = await fetchYowanAdminMailSummary();

        if (isMounted) {
          setAdminMailSummary(payload);
        }
      } catch (error) {
        if (isMounted) {
          setAdminMailSummary(null);
          setAdminMailSummaryError(content?.adminMailSummaryLoadErrorMessage || '管理者メール状況を読み込めませんでした。');
        }
      } finally {
        if (isMounted) {
          setIsAdminMailSummaryLoading(false);
        }
      }
    };

    loadAdminMailSummary();

    return () => {
      isMounted = false;
    };
  }, [canAccessAdminMail, content?.adminMailSummaryLoadErrorMessage, guard.status]);

  if (!content) {
    return null;
  }

  const handleSectionSubmit = async (section, event) => {
    event.preventDefault();

    const sectionValues = formValues[section.title] || {};
    const nextErrors = buildValidationErrors(section, sectionValues);

    setFormErrors((currentErrors) => ({
      ...currentErrors,
      [section.title]: nextErrors,
    }));
    setFormMessages((currentMessages) => ({
      ...currentMessages,
      [section.title]: '',
    }));
    setAccountNotice('');

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setSubmittingSection(section.title);

    try {
      if (section.kind === 'emailChange') {
        const payload = await changeYowanLoginEmail({
          currentPassword: sectionValues.currentPassword,
          newEmail: sectionValues.newEmail,
        });

        try {
          await syncAuth();
        } catch (syncError) {
          // The mutation has already succeeded; keep the success UX and let the next auth sync refresh state.
        }

        setMemberProfile((currentProfile) =>
          currentProfile
            ? {
                ...currentProfile,
                email: payload.email || sectionValues.newEmail,
              }
            : currentProfile
        );
        setFormValues((currentValues) => ({
          ...currentValues,
          [section.title]: buildEmptyFormValues(section),
        }));
        setFormMessages((currentMessages) => ({
          ...currentMessages,
          [section.title]: section.form.successMessage,
        }));
        return;
      }

      if (section.kind === 'passwordChange') {
        await changeYowanPassword({
          currentPassword: sectionValues.currentPassword,
          newPassword: sectionValues.newPassword,
        });

        setFormValues((currentValues) => ({
          ...currentValues,
          [section.title]: buildEmptyFormValues(section),
        }));
        setAccountNotice(section.form.successMessage);
        refreshAuth();
        return;
      }

      setFormMessages((currentMessages) => ({
        ...currentMessages,
        [section.title]: section.form.pendingMessage || '',
      }));
    } catch (error) {
      const responseData = error?.response?.data || {};
      const isDuplicateEmail =
        section.kind === 'emailChange' &&
        error?.response?.status === 409 &&
        responseData.reason === 'duplicate_email';

      setFormMessages((currentMessages) => ({
        ...currentMessages,
        [section.title]: isDuplicateEmail
          ? section.form.duplicateMessage
          : section.form.errorMessage,
      }));
    } finally {
      setSubmittingSection('');
    }
  };

  const handleAccountPreferenceChange = async (category, checked) => {
    if (guard.status !== 'ready' || submittingPreference) {
      return;
    }

    const nextNews = category === 'news'
      ? checked
      : getPreferenceEnabled(accountPreferences, 'news');
    const nextBlog = category === 'blog'
      ? checked
      : getPreferenceEnabled(accountPreferences, 'blog');

    setSubmittingPreference(category);
    setAccountPreferenceError('');
    setAccountPreferenceMessage('');

    try {
      const payload = await updateYowanAccountNotificationPreferences({
        news: nextNews,
        blog: nextBlog,
      });

      setAccountPreferences(payload);
      setAccountPreferenceMessage(content?.accountNotificationUpdatedMessage || 'メール配信設定を更新しました。');
    } catch (error) {
      setAccountPreferenceError(content?.accountNotificationUpdateErrorMessage || 'メール配信設定を更新できませんでした。');
    } finally {
      setSubmittingPreference('');
    }
  };

  const membershipLabels = buildMembershipLabels({
    currentUser,
    auth: guard.auth,
    memberProfile,
  });
  const loginEmail = resolveLoginEmail({
    currentUser,
    auth: guard.auth,
    memberProfile,
  });
  const isPreferenceDisabled = guard.status !== 'ready' || isAccountPreferenceLoading || Boolean(submittingPreference);
  const canCreateMissingProfile =
    guard.status === 'ready' && isMemberProfileMissing && isOperatorProfileMissing(guard.auth);
  const profileIsUnavailable =
    guard.status === 'ready' && !isMemberProfileLoading && (isMemberProfileMissing || memberProfileLoadError);
  const profileActionDisabled =
    guard.status === 'ready' && (isMemberProfileLoading || (profileIsUnavailable && !canCreateMissingProfile));
  const adminMailUnreadLabel = formatUnreadCountLabel(adminMailSummary?.unreadCount);

  return (
    <>
      <Section>
        <PageContainer>
          <div className="yowan-mypage-shell">
            <div className="yowan-mypage-header">
              <Heading as="h1" align="left">
                {content.header.title}
              </Heading>
              {content.header.paragraphs.map((paragraph) => (
                <p key={paragraph} className="yowan-mypage-header__lead">
                  {paragraph}
                </p>
              ))}
              {guard.notice ? (
                <p className="yowan-mypage-header__lead">
                  {guard.notice.message}
                  <br />
                  <Link to={guard.notice.actionHref}>{guard.notice.actionLabel}</Link>
                </p>
              ) : null}
              {accountNotice ? (
                <p className="yowan-mypage-header__lead">
                  {accountNotice}
                  <br />
                  <Link to={appendYowanPreviewQueryIfNeeded('/login', location.search)}>ログイン画面へ</Link>
                </p>
              ) : null}
            </div>

            <div className="yowan-mypage-sections">
              {content.sections.map((section) => {
                const stakeholderMenuLinks = section.kind === 'stakeholderMenu'
                  ? buildStakeholderMenuLinks(section, currentUser)
                  : [];
                const blogAuthorMenuLinks = section.kind === 'blogAuthorMenu'
                  ? buildBlogAuthorMenuLinks(section, currentUser)
                  : [];

                if (section.kind === 'stakeholderMenu' && stakeholderMenuLinks.length === 0) {
                  return null;
                }

                if (section.kind === 'blogAuthorMenu' && blogAuthorMenuLinks.length === 0) {
                  return null;
                }

                if (section.kind === 'adminMailMenu' && !canAccessAdminMail) {
                  return null;
                }

                return (
                <section key={section.title} className="yowan-mypage-section">
                  <div className="yowan-mypage-section__label">
                    <h2 className="yowan-mypage-section__title">{section.title}</h2>
                    {section.lead ? <p className="yowan-mypage-section__lead">{section.lead}</p> : null}
                  </div>

                  <div className="yowan-mypage-section__content">
                    {section.kind === 'accountSummary' ? (
                      <dl className="yowan-mypage-summary__grid yowan-mypage-account-summary">
                        {buildAccountSummaryItems({ section, loginEmail, membershipLabels }).map((item) => (
                          <div key={item.label} className="yowan-mypage-summary__item">
                            <dt>{item.label}</dt>
                            <dd>
                              <input
                                className="c-form__control yowan-mypage-summary__control"
                                type="text"
                                value={item.value || '-'}
                                aria-label={item.label}
                                readOnly
                              />
                            </dd>
                          </div>
                        ))}
                      </dl>
                    ) : null}

                    {section.kind === 'profileSummary' ? (
                      <>
                        {isMemberProfileLoading ? <LoadingSpinner size="sm" /> : null}
                        {memberProfile && !profileIsUnavailable ? (
                          <dl className="yowan-mypage-summary__grid">
                            {buildProfileSummaryItems(section.fields, memberProfile).map((item) => (
                              <div key={item.label} className="yowan-mypage-summary__item">
                                <dt>{item.label}</dt>
                                <dd>
                                  <input
                                    className="c-form__control yowan-mypage-summary__control"
                                    type="text"
                                    value={item.value || '-'}
                                    aria-label={item.label}
                                    readOnly
                                  />
                                </dd>
                              </div>
                            ))}
                          </dl>
                        ) : null}
                        {profileIsUnavailable ? (
                          <div className="yowan-mypage-profile-lock">
                            <p className="yowan-mypage-profile-lock__title">{section.unavailableTitle}</p>
                            <p className="yowan-mypage-profile-lock__message">
                              {memberProfileLoadError || (
                                canCreateMissingProfile
                                  ? section.operatorProfileMissingMessage || section.unavailableMessage
                                  : section.unavailableMessage
                              )}
                            </p>
                            {canCreateMissingProfile ? (
                              <Link
                                className="c-form__submit yowan-mypage-section__action"
                                to={appendYowanPreviewQueryIfNeeded(section.href, location.search)}
                              >
                                {section.createCtaLabel || section.ctaLabel}
                              </Link>
                            ) : null}
                          </div>
                        ) : null}
                      </>
                    ) : null}

                    {section.kind === 'membershipSummary' ? (
                      <div className="yowan-mypage-type">
                        <div className="yowan-mypage-type__list" aria-label={section.title}>
                          {membershipLabels.map((label) => (
                            <div key={label} className="yowan-mypage-type__item">
                              <span className="yowan-mypage-type__icon" aria-hidden="true" />
                              <span className="yowan-mypage-type__label">{label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {section.kind === 'stakeholderMenu' ? (
                      <div className="yowan-mypage-stakeholder-menu">
                        {section.paragraphs
                          ? section.paragraphs.map((paragraph) => (
                              <p key={paragraph} className="yowan-mypage-section__paragraph">
                                {paragraph}
                              </p>
                            ))
                          : null}
                        <div className="yowan-mypage-stakeholder-menu__links">
                          {stakeholderMenuLinks.map((link) => (
                            <Link
                              key={link.href}
                              className="c-form__submit yowan-mypage-section__action"
                              to={appendYowanPreviewQueryIfNeeded(link.href, location.search)}
                            >
                              {link.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {section.kind === 'blogAuthorMenu' ? (
                      <div className="yowan-mypage-stakeholder-menu">
                        {section.paragraphs
                          ? section.paragraphs.map((paragraph) => (
                              <p key={paragraph} className="yowan-mypage-section__paragraph">
                                {paragraph}
                              </p>
                            ))
                          : null}
                        <div className="yowan-mypage-stakeholder-menu__links">
                          {blogAuthorMenuLinks.map((link) => (
                            <Link
                              key={link.href}
                              className="c-form__submit yowan-mypage-section__action"
                              to={appendYowanPreviewQueryIfNeeded(link.href, location.search)}
                            >
                              {link.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {section.kind === 'adminMailMenu' ? (
                      <div className="yowan-mypage-admin-mail">
                        {isAdminMailSummaryLoading ? <LoadingSpinner size="sm" /> : null}
                        {section.paragraphs
                          ? section.paragraphs.map((paragraph) => (
                              <p key={paragraph} className="yowan-mypage-section__paragraph">
                                {paragraph}
                              </p>
                            ))
                          : null}
                        {adminMailSummary ? (
                          <dl className="yowan-mypage-admin-mail__summary">
                            <div>
                              <dt>{section.unreadLabel}</dt>
                              <dd>{adminMailSummary.unreadCount}</dd>
                            </div>
                            <div>
                              <dt>{section.needsResponseLabel}</dt>
                              <dd>{adminMailSummary.needsResponseCount}</dd>
                            </div>
                            <div>
                              <dt>{section.openThreadLabel}</dt>
                              <dd>{adminMailSummary.openThreadCount}</dd>
                            </div>
                          </dl>
                        ) : null}
                        {adminMailUnreadLabel ? (
                          <p className="yowan-mypage-admin-mail__notice">
                            {section.unreadBadgeLabel.replace('{count}', adminMailUnreadLabel)}
                          </p>
                        ) : adminMailSummary && !isAdminMailSummaryLoading ? (
                          <p className="yowan-mypage-admin-mail__notice">{section.emptySummaryText}</p>
                        ) : null}
                        {adminMailSummaryError ? (
                          <p className="yowan-mypage-section__error">{adminMailSummaryError}</p>
                        ) : null}
                        <Link
                          className="c-form__submit yowan-mypage-section__action"
                          to={appendYowanPreviewQueryIfNeeded(section.linkHref, location.search)}
                        >
                          {section.linkLabel}
                        </Link>
                      </div>
                    ) : null}

                    {section.kind === 'notificationPreferences' ? (
                      <div className="yowan-mypage-notification">
                        {isAccountPreferenceLoading ? <LoadingSpinner size="sm" /> : null}
                        {section.paragraphs
                          ? section.paragraphs.map((paragraph) => (
                              <p key={paragraph} className="yowan-mypage-section__paragraph">
                                {paragraph}
                              </p>
                            ))
                          : null}
                        <div className="yowan-mypage-notification__controls">
                          {(section.preferences || []).map((preference) => {
                            const isChecked = getPreferenceEnabled(accountPreferences, preference.key);
                            const isSubmitting = submittingPreference === preference.key;

                            return (
                              <label key={preference.key} className="yowan-mypage-notification__control">
                                <span className="yowan-mypage-notification__label">{preference.label}</span>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  disabled={isPreferenceDisabled}
                                  onChange={(event) => handleAccountPreferenceChange(preference.key, event.target.checked)}
                                />
                                <span className="yowan-mypage-notification__state">
                                  {isSubmitting
                                    ? section.submittingLabel
                                    : isChecked
                                      ? section.enabledLabel
                                      : section.disabledLabel}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                        {accountPreferenceError ? (
                          <p className="yowan-mypage-section__error">{accountPreferenceError}</p>
                        ) : null}
                        {accountPreferenceMessage ? (
                          <p className="yowan-mypage-section__message">{accountPreferenceMessage}</p>
                        ) : null}
                      </div>
                    ) : null}

                    {section.kind !== 'stakeholderMenu' &&
                    section.kind !== 'blogAuthorMenu' &&
                    section.kind !== 'adminMailMenu' &&
                    section.kind !== 'notificationPreferences' &&
                    section.paragraphs
                      ? section.paragraphs.map((paragraph) => (
                          <p key={paragraph} className="yowan-mypage-section__paragraph">
                            {paragraph}
                          </p>
                        ))
                      : null}

                    {section.href &&
                    section.ctaLabel &&
                    !profileActionDisabled &&
                    !(section.kind === 'profileSummary' && profileIsUnavailable) ? (
                      <Link
                        className="c-form__submit yowan-mypage-section__action"
                        to={appendYowanPreviewQueryIfNeeded(section.href, location.search)}
                      >
                        {section.ctaLabel}
                      </Link>
                    ) : null}

                    {section.href &&
                    section.ctaLabel &&
                    profileActionDisabled &&
                    !(section.kind === 'profileSummary' && profileIsUnavailable && canCreateMissingProfile) ? (
                      <button
                        className="c-form__submit yowan-mypage-section__action yowan-mypage-section__action--disabled"
                        type="button"
                        disabled
                      >
                        {section.ctaLabel}
                      </button>
                    ) : null}

                    {section.form && guard.status !== 'ready' ? (
                      <p className="yowan-mypage-section__paragraph">
                        この操作は、認証 API 接続後にログイン利用者向けに有効化する予定です。
                      </p>
                    ) : null}

                    {section.form && guard.status === 'ready' ? (
                      <form
                        className="yowan-mypage-section__form c-form"
                        onSubmit={(event) => handleSectionSubmit(section, event)}
                      >
                        {Object.entries(section.form.labels).map(([fieldKey, label]) => (
                          <label key={fieldKey} className="c-form__field">
                            <span className="c-form__label">{label}</span>
                            <input
                              className="c-form__control"
                              type={getFieldInputType(fieldKey)}
                              value={formValues[section.title]?.[fieldKey] || ''}
                              onChange={(event) => {
                                const nextValue = event.target.value;

                                setFormValues((currentValues) => ({
                                  ...currentValues,
                                  [section.title]: {
                                    ...(currentValues[section.title] || {}),
                                    [fieldKey]: nextValue,
                                  },
                                }));

                                setFormErrors((currentErrors) => ({
                                  ...currentErrors,
                                  [section.title]: {
                                    ...(currentErrors[section.title] || {}),
                                    [fieldKey]: '',
                                  },
                                }));
                              }}
                            />
                            {formErrors[section.title]?.[fieldKey] ? (
                              <em className="yowan-login-form__error">
                                {formErrors[section.title][fieldKey]}
                              </em>
                            ) : null}
                          </label>
                        ))}

                        <button
                          type="submit"
                          className="c-form__submit yowan-mypage-section__submit"
                          disabled={submittingSection === section.title}
                        >
                          {submittingSection === section.title
                            ? section.form.submittingLabel
                            : section.form.buttonLabel}
                        </button>

                        {formMessages[section.title] ? (
                          <p className="yowan-login-form__message">{formMessages[section.title]}</p>
                        ) : null}
                      </form>
                    ) : null}
                  </div>
                </section>
                );
              })}
            </div>
          </div>
        </PageContainer>
      </Section>
    </>
  );
};

export default MyPageTopPage;
